import React, { useContext, useState } from 'react'

import {
  DirectBankLinking,
  DirectBankLinkingRedirect,
  DirectLinkingState,
  useAccountState,
} from '@acorns/web-bank-linking'
import debug from 'debug'
import { compose } from 'recompose'

import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import { ActionType, PageName } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorHandler } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import {
  WithSetPrimaryFundingSourceProps,
  withSetPrimaryFundingSource,
} from 'utils/mutators/bank-linking/bankLinking'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'

import {
  ButtonCtaType,
  resolveLinkingButtonTappedEvent,
  resolveLinkingScreenViewedEvent,
} from './events'

const logger = debug('institutions')

const enhance = compose(
  withFlowRouter,
  withPartnerProps,
  withSetPrimaryFundingSource,
  withOptimizely,
  withApolloErrorHandler('RegisterLinkSession', () => () => false),
)

type Props = WithFlowRouterProps &
  WithPartnerProps &
  WithOptimizelyProps &
  WithSetPrimaryFundingSourceProps

export const DBILinkComponent: React.FC<Props> = ({
  route,
  partner,
  setPrimaryFundingSource,
  trackOptimizely,
  flow,
}) => {
  const {
    financialInstitution: { name: institutionName },
  } = useAccountState()
  const [directLinkingState, setDirectLinkingState] = useState(DirectLinkingState.ready)

  const trackStartLink = () => {
    trackEvent('Button Tapped', {
      eventData: resolveLinkingButtonTappedEvent(
        DirectLinkingState.ready,
        ButtonCtaType.start,
        institutionName,
      ),
      partner,
      flow,
    })
  }

  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)

  const trackStateChange = (linkingState: DirectLinkingState) => {
    setDirectLinkingState(linkingState)

    const eventData = resolveLinkingScreenViewedEvent(linkingState, institutionName)

    if (eventData) {
      trackEvent('Screen Viewed', {
        eventData,
        partner,
        flow,
      })
    }
  }

  const handleAvailableFundingSource = async ({ financialInstitution, linkedSubaccounts }) => {
    if (linkedSubaccounts.length > 1) {
      logger('More than 1 available funding source')
      isDynamicFunnelEnabled
        ? routeToPage(PageName.DirectBankIntegration, ActionType.Choose, {
            financialInstitution,
            linkedSubaccounts,
          })
        : route({
            from: FlowRoute.DirectBankIntegration,
            action: 'choose',
            locationState: { financialInstitution, linkedSubaccounts },
          })
    } else if (linkedSubaccounts.length === 1) {
      logger('1 available funding source')
      const { id: linkedSubaccountId } = linkedSubaccounts[0]

      await setPrimaryFundingSource(linkedSubaccountId).then(() => {
        trackOptimizely('funding_source.linked')
        trackEvent('PlaidLink:Success', { location, partner, flow })
        route({
          from: FlowRoute.DirectBankIntegration,
          action: 'continue',
          locationState: { financialInstitution, linkedSubaccounts },
        })
      })
    }
  }

  const handleManualLink = () => {
    const eventData = resolveLinkingButtonTappedEvent(
      directLinkingState,
      ButtonCtaType.manualLink,
      institutionName,
    )

    if (eventData) {
      trackEvent('Button Tapped', {
        eventData,
        partner,
        flow,
      })
    }

    isDynamicFunnelEnabled
      ? routeToPage(PageName.DirectBankIntegration, ActionType.Manual)
      : route({
          from: FlowRoute.DirectBankIntegration,
          action: 'manual',
        })
  }

  const handleTryAgain = () => {
    const eventData = resolveLinkingButtonTappedEvent(
      directLinkingState,
      ButtonCtaType.tryAgain,
      institutionName,
    )

    if (eventData) {
      trackEvent('Button Tapped', {
        eventData,
        partner,
        flow,
      })
    }

    route({
      from: FlowRoute.DirectBankIntegration,
      action: 'different-bank',
    })
  }

  const getNoCheckingText = (name: string) => {
    const linkedForCopy = flow === 'later' ? '' : ' that can be used for Round-Ups®'
    return `We linked your accounts from ${name}${linkedForCopy}. However, no checking accounts were found with that bank. A valid checking account is required as funding source`
  }

  return (
    <>
      <Nav />

      <Layout fullWidth>
        <DirectBankLinking
          funding={true}
          getNoCheckingText={getNoCheckingText}
          onAvailableFundingSources={handleAvailableFundingSource}
          onDirectLinkingStateChange={trackStateChange}
          onManualLink={handleManualLink}
          onStartLink={trackStartLink}
          onTryAgain={handleTryAgain}
          redirectUrl={`${window.location.origin}${FlowRoute.DirectBankIntegrationRedirect}`}
        />
      </Layout>
    </>
  )
}

export const DirectBankIntegration = enhance(DBILinkComponent)

export const DirectBankIntegrationRedirect = () => (
  <DirectBankLinkingRedirect fallbackUrl={`${window.location.origin}/institution`} />
)
